import { FC, Fragment, useState } from 'react';
import { observer } from 'mobx-react';

import { Banner, BodyText, Modal, Stack } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';

import { AppClientResponse, ConnectedAppSecretResponse } from '../../api/app.api';
import { CopyBox, CustomModal } from '../../common';
import { AppClientListPageStore } from '../stores/app-client-list-page.store';
import { SecretList } from './secret-list';

const getResourceOwnerName = (client: AppClientResponse) => {
    const name = `${client.resourceOwnerTerm ?? 'Tenant'} ID ${client.resourceOwner}`;
    return client.environmentName ? `${name} (${client.environmentName})` : name;
};

const ManageSecretsDialog: FC<{
    client: AppClientResponse;
    onGenerateClick: () => void;
    onDeactivateClick: (secret: ConnectedAppSecretResponse) => void;
    onClose: () => void;
}> = ({ client, onGenerateClick, onDeactivateClick, onClose }) => {
    return (
        <CustomModal
            size={Modal.Sizes.M}
            title={`Manage Client Secrets - ${getResourceOwnerName(client)}`}
            subtitle={`Client ID ${client.clientId}`}
            primaryActionName="Close"
            onPrimaryActionClick={onClose}
        >
            <SecretList
                secrets={client.clientSecrets}
                onGenerateClick={onGenerateClick}
                onDeactivateClick={onDeactivateClick}
            />
        </CustomModal>
    );
};
ManageSecretsDialog.displayName = 'ManageSecretsDialog';

const GenerateSecretDialog: FC<{
    client: AppClientResponse;
    onGenerateClick: () => Promise<void>;
    onClose: () => void;
}> = ({ client, onGenerateClick, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const handleGenerateClick = async () => {
        setLoading(true);
        try {
            await onGenerateClick();
        } catch {
            setErrorMessage('There was a problem generating the secret.');
            setLoading(false);
        }
    };

    return (
        <CustomModal
            dialog
            size={Modal.Sizes.S}
            title={`Generate Client Secret - ${getResourceOwnerName(client)}`}
            subtitle={`Client ID ${client.clientId}`}
            primaryActionName="Yes, Continue"
            primaryActionLoading={loading}
            onPrimaryActionClick={handleGenerateClick}
            secondaryActionName="Cancel"
            onSecondaryActionClick={onClose}
            errorMessage={errorMessage}
        >
            <BodyText>
                Once a client secret is generated, it's visible only once. Be sure you are ready to
                to configure it within your application.
            </BodyText>
            <BodyText>Would you like to continue?</BodyText>
        </CustomModal>
    );
};
GenerateSecretDialog.displayName = 'GenerateSecretDialog';

const ViewSecretDialog: FC<{
    client: AppClientResponse;
    secretValue: string;
    onClose: () => void;
}> = ({ client, secretValue, onClose }) => {
    return (
        <CustomModal
            size={Modal.Sizes.M}
            title={`New Client Secret - ${getResourceOwnerName(client)}`}
            subtitle={`Client ID ${client.clientId}`}
            primaryActionName="Close"
            onPrimaryActionClick={onClose}
        >
            <Banner
                status="warning"
                icon
                title="This client secret is visible only once. If you lose it, you'll need to generate a new secret."
            />
            <Stack direction="column">
                <BodyText bold>Client Secret</BodyText>
                <CopyBox size={65} value={secretValue} />
            </Stack>
        </CustomModal>
    );
};
ViewSecretDialog.displayName = 'ViewSecretDialog';

const DeactivateSecretDialog: FC<{
    client: AppClientResponse;
    secret: ConnectedAppSecretResponse;
    onDeactivateClick: () => Promise<void>;
    onClose: () => void;
}> = ({ client, secret, onDeactivateClick, onClose }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const handleDeactivateClick = async (): Promise<void> => {
        setLoading(true);
        try {
            await onDeactivateClick();
        } catch {
            setErrorMessage('There was a problem deactivating the secret.');
            setLoading(false);
        }
    };

    return (
        <CustomModal
            dialog
            size={Modal.Sizes.S}
            title={`Deactivate Client Secret - ${getResourceOwnerName(client)}`}
            subtitle={`Client ID ${client.clientId}`}
            primaryActionName="Deactivate"
            primaryActionNegative
            primaryActionLoading={loading}
            onPrimaryActionClick={handleDeactivateClick}
            secondaryActionName="Cancel"
            onSecondaryActionClick={onClose}
            errorMessage={errorMessage}
        >
            <BodyText>
                If you deactivate client secret "{secret.valuePrefix}...", it will no longer be
                valid. Are you sure you want to continue?
            </BodyText>
        </CustomModal>
    );
};
DeactivateSecretDialog.displayName = 'DeactivateSecretDialog';

export const SecretManager: FC = observer(() => {
    const [store] = useDependencies(AppClientListPageStore);
    const client = store.selectedClient!;
    const [isManageSecretsDialogOpen] = useState(client.clientSecrets.length > 0);
    const [isGenerateSecretDialogOpen, setIsGenerateSecretDialogOpen] = useState(
        client.clientSecrets.length === 0,
    );
    const [newSecretValue, setNewSecretValue] = useState('');
    const [secretToDelete, setSecretToDelete] = useState<ConnectedAppSecretResponse | null>(null);

    return (
        <Fragment>
            {isManageSecretsDialogOpen && (
                <ManageSecretsDialog
                    client={client}
                    onGenerateClick={() => {
                        setIsGenerateSecretDialogOpen(true);
                    }}
                    onDeactivateClick={secret => {
                        setSecretToDelete(secret);
                    }}
                    onClose={() => {
                        store.selectClient(null);
                    }}
                />
            )}
            {isGenerateSecretDialogOpen && (
                <GenerateSecretDialog
                    client={client}
                    onGenerateClick={async () => {
                        setNewSecretValue(await store.generateClientSecret());
                        setIsGenerateSecretDialogOpen(false);
                    }}
                    onClose={() => {
                        if (isManageSecretsDialogOpen) {
                            setIsGenerateSecretDialogOpen(false);
                        } else {
                            store.selectClient(null);
                        }
                    }}
                />
            )}
            {newSecretValue && (
                <ViewSecretDialog
                    client={client}
                    secretValue={newSecretValue}
                    onClose={() => {
                        setNewSecretValue('');
                        if (!isManageSecretsDialogOpen) {
                            store.selectClient(null);
                        }
                    }}
                />
            )}
            {secretToDelete && (
                <DeactivateSecretDialog
                    client={client}
                    secret={secretToDelete}
                    onDeactivateClick={async () => {
                        await store.deactivateClientSecret(secretToDelete.id);
                        setSecretToDelete(null);
                    }}
                    onClose={() => {
                        setSecretToDelete(null);
                    }}
                />
            )}
        </Fragment>
    );
});
SecretManager.displayName = 'SecretManager';
